export type Division = "DI" | "DII" | "DIII";
export type Status = "unranked" | "keep" | "maybe" | "cut";
export type HouseStatus = "opted_in" | "opted_out" | "not_applicable";

export interface ScorecardSlice {
  label: string;
  value: number;
}

/**
 * A hand-authored deep-dive on a school, filled in on request for select schools
 * (data/ai-summaries/<school-id>.json, merged into the seed at build:seed).
 *
 * Authoring rules (see also data/ai-summaries/_authoring-notes.md):
 *  - Never mention coxswain / coxing. Katie will not cox; a rowing seat is the point.
 *  - Make the roster-fit point SPECIFIC to the school (its competitive level, roster
 *    depth, culture) — not generic "DI recruits tall rowers" boilerplate that reads the
 *    same everywhere. A deep developmental bottom-of-conference program and a win-now
 *    ranked program give very different answers for a 5'4" rower.
 *  - Height data is rarely published; acknowledge the uncertainty and say to ask the coach.
 *  - Every summary is about the school's OPEN-WEIGHT women's rowing program only. Never
 *    mention lightweight rowing, weight classes, or weight in any form — not even to note
 *    that a school fields a lightweight crew. Treat lightweight rowing as nonexistent.
 *  - Only raise D2/D3 where it adds real signal for that school, not by reflex.
 *  - Lead with her intended major (physical therapy / DPT).
 *  - No mention of any AI vendor in the text. Keep it honest, specific, sourced.
 */
export interface AiSummary {
  /** ISO date the summary was written / last refreshed */
  updated: string;
  /** 1–3 sentence bottom line */
  headline: string;
  sections: { heading: string; body: string }[];
  /** URLs consulted */
  sources?: string[];
}

export interface Evaluation {
  status: Status;
  /** Katie's own rank — a free decimal (e.g. 3.2); lower = better. Sort the list by it. */
  katieRank: number | null;
  notes: string;
  visitPlanned: boolean;
  visitDate: string | null;
  /** Dad's own shortlist flag — he ticks it and explains why in dadPickWhy. */
  dadPick: boolean;
  dadPickWhy: string;
  updatedAt: string | null;
}

export type EvaluationPatch = Partial<Evaluation>;

/** A named snapshot of the list-page filter controls, saved server-side. */
export interface FilterPreset {
  name: string;
  q: string;
  div: string[];
  prox: string[];
  tier: string[];
  status: string[];
  schol: string[];
  love: string[];
  pt: string[];
  dadOnly: boolean;
  claudeOnly: boolean;
  hideCut: boolean;
}

export type InteractionType =
  | "email_sent"
  | "email_received"
  | "call"
  | "meeting"
  | "visit"
  | "form_submitted"
  | "questionnaire"
  | "other";

export interface Interaction {
  id: string;
  date: string;
  time: string | null;
  type: InteractionType;
  who: string;
  subject: string;
  body: string;
  followUpDate: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface School {
  id: string;
  name: string;
  division: Division;
  conference: string | null;
  conference_note: string | null;
  state: string | null;
  city: string | null;
  distance_bucket: string | null;
  athletic_dollar_potential: string | null;
  scholarship_ceiling: string | null;
  scholarship_ceiling_max: number | null;
  house_settlement_status: HouseStatus;
  house_settlement_verified: boolean;
  house_settlement_note: string | null;
  program_notes: string | null;
  v8_race_time: string | null;
  v8_race_time_source: string | null;
  cmax_rank_div: number | null;
  cmax_rank_div_raw: string | null;
  cmax_secs_back_div: number | null;
  cmax_rank_combined: number | null;
  cmax_rank_combined_raw: string | null;
  cmax_secs_back_combined: number | null;
  cmax_estimated: boolean;
  cmax_note: string | null;
  est_8_2k_time: string | null;
  rowing_fit_tier_seed: string | null;
  school_website: string | null;
  rowing_website: string | null;
  head_coach_name: string | null;
  head_coach_email: string | null;
  head_coach_phone: string | null;
  recruiting_form_url: string | null;
  scorecard_id: string | null;
  scorecard_url: string | null;
  ownership_type: string | null;
  grad_rate: number | null;
  grad_rate_4yr: number | null;
  retention_rate: number | null;
  admission_rate: number | null;
  test_policy: string | null;
  undergrad_size: number | null;
  grad_students: number | null;
  part_time_share: number | null;
  share_25_older: number | null;
  pct_women: number | null;
  first_gen_share: number | null;
  median_family_income: number | null;
  student_love: number | null;
  retention_vs_expected: number | null;
  retention_expected: number | null;
  student_love_basis: string | null;
  race_mix: ScorecardSlice[] | null;
  net_price: number | null;
  net_price_by_income: ScorecardSlice[] | null;
  tuition_in_state: number | null;
  tuition_out_state: number | null;
  sat_avg: number | null;
  sat_25: number | null;
  sat_75: number | null;
  act_median: number | null;
  act_25: number | null;
  act_75: number | null;
  avg_gpa: number | null;
  cost_attendance: number | null;
  pct_pell: number | null;
  federal_loan_rate: number | null;
  median_debt: number | null;
  monthly_loan_payment: number | null;
  earnings_10yr: number | null;
  earnings_above_hs_grad: number | null;
  fields_of_study: ScorecardSlice[] | null;
  undergrad_majors: string[] | null;
  /** "has_pt" = clinical rehab/therapy doctorate (proxy for a DPT); "related" = pre-PT majors only */
  pt_program: "has_pt" | "related" | null;
  pt_majors: string[] | null;
  ai_summary: AiSummary | null;
  /** Claude's top-25 picks for Katie (data/claude-picks.json). rank 1 = best fit. */
  claude_pick: { rank: number; note: string } | null;
  locale: string | null;
  size_category: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  logo_url: string | null;
  athletics_domain: string | null;
  scholarship_dollars: number | null;
  scholarship_score: number | null;
  scholarship_funding_level: string | null;
  scholarship_label: string | null;
  scholarship_basis: string | null;
  scholarship_cap_label: string | null;
  data_flags: string[];
  sources: Record<string, string>;
  _edited?: string[];
  evaluation: Evaluation;
  interactions: Interaction[];
}

export interface TierThresholds {
  /** below this many cMax seconds back -> "Dream" (their 2V is well ahead of her pace) */
  dream: number;
  /** below this -> "Stretch" (the amber in-between band); at/above -> "Target Range" */
  stretch: number;
  /** at/below this -> "Target Range"; above -> "Likely Floor" */
  target: number;
}

export interface Settings {
  assumptions: {
    ergPr: string;
    ergProjected: string;
    v2Time: string;
    v2Projected: string;
    tierThresholds: TierThresholds;
  };
}

export const STATUS_LABELS: Record<Status, string> = {
  unranked: "Unranked",
  keep: "Keep",
  maybe: "Maybe",
  cut: "Cut",
};

export const INTERACTION_LABELS: Record<InteractionType, string> = {
  email_sent: "Email sent",
  email_received: "Email received",
  call: "Phone call",
  meeting: "Meeting",
  visit: "Campus visit",
  form_submitted: "Form submitted",
  questionnaire: "Recruiting questionnaire",
  other: "Other",
};

// ---- /pt page (PT clinic micro-internship list) --------------------------------------
export type ClinicStatus = "unranked" | "keep" | "cut";
export type ClinicType = "independent" | "small-group" | "hospital-outpatient" | "chain";

export interface ClinicEvaluation {
  status: ClinicStatus;
  notes: string;
  updatedAt: string | null;
}

export interface Clinic {
  id: string;
  name: string;
  type: ClinicType;
  specialty: string;
  address: string;
  city: string;
  zip: string;
  /** Real driving miles from the boathouse (245 Azalea Dr, Roswell), via OSRM */
  distance_mi: number;
  phone: string;
  website: string;
  claude_rank: number;
  claude_note: string;
  lat: number;
  lon: number;
  evaluation: ClinicEvaluation;
}

/** Fixed reference pins on the /pt Map tab (home, school, boathouse) - static, no evaluation. */
export type PlaceKind = "home" | "school" | "boathouse";

export interface Place {
  id: string;
  name: string;
  kind: PlaceKind;
  address: string;
  city: string;
  zip: string;
  lat: number;
  lon: number;
}
